import type { PriceUnitDto } from '@/api';
import { formatDecimal } from '@/components/table/table-format-helpers';
import { useSimulationErrorNotification } from '@/hooks/use-simulation-error-notification';
import { themeColors } from '@/theme/colors';
import { filterNumeric } from '@/utils/number';
import {
  Button,
  Checkbox,
  ContentSwitcher,
  Modal,
  TextField,
} from '@lego/connect-components-react';
import { Flex } from '@lego/klik-ui';
import {
  type CSSProperties,
  Fragment,
  type RefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSavePricelistSettings } from '../../../simulation-queries';
import type { PricelistSettings } from '../../utils/simulation-types';
import {
  CONTENT_SWITCHER_WIDTH,
  DEFAULT_SAME_FOR_ALL,
  type FieldKey,
  GAP,
  LABEL_COL_WIDTH,
  MODAL_CONTENT_PADDING,
  PRICE_SETTINGS_FIELDS,
  type PriceSettingsPane,
  type PriceSettingsValues,
  THRESHOLD_FIELD_KEYS,
  VALIDATION_RULES,
  VALUE_COL_WIDTH,
  computeOrderedPriceUnits,
  getFieldsForPane,
  parseValue,
  toDraftValues,
} from './price-settings-utils';

type PriceSettingsModalProps = {
  isOpen: boolean;
  triggerRef: RefObject<HTMLElement | null>;
  onClose: () => void;
  simulationId: number;
  pricelistSettings: Map<string, PricelistSettings>;
  priceUnits: PriceUnitDto[];
  onApply: (updated: Map<string, PricelistSettings>) => void;
};

const PriceSettingsModal = ({
  isOpen,
  triggerRef,
  onClose,
  simulationId,
  pricelistSettings,
  priceUnits,
  onApply,
}: PriceSettingsModalProps) => {
  const showSimulationError = useSimulationErrorNotification();
  const { mutateAsync: savePricelistSettings, isPending } =
    useSavePricelistSettings(simulationId, (error) => {
      showSimulationError(error, 'save price settings');
    });
  const prevIsOpenRef = useRef(false);
  const preSameForAllSnapshot = useRef<
    Partial<Record<FieldKey, Record<string, string>>>
  >({});
  const [draftByPriceUnit, setDraftByPriceUnit] = useState<
    Record<string, PriceSettingsValues>
  >({});
  const [sameForAll, setSameForAll] =
    useState<Record<FieldKey, boolean>>(DEFAULT_SAME_FOR_ALL);
  const [activePane, setActivePane] = useState<PriceSettingsPane>('settings');
  const isThresholdPane = activePane === 'thresholds';

  const orderedPriceUnits = useMemo(
    () => computeOrderedPriceUnits(priceUnits, pricelistSettings),
    [priceUnits, pricelistSettings],
  );

  const pricelistCount = orderedPriceUnits.length;
  const showCheckboxes = pricelistCount >= 2;
  const estimatedGridWidth =
    LABEL_COL_WIDTH + pricelistCount * VALUE_COL_WIDTH + pricelistCount * GAP;
  const estimatedModalWidth = estimatedGridWidth + MODAL_CONTENT_PADDING;
  const modalSize =
    estimatedModalWidth <= 480
      ? 'small'
      : estimatedModalWidth <= 640
        ? 'medium'
        : estimatedModalWidth <= 800
          ? 'large'
          : 'x-large';

  const [isDraftInitialized, setIsDraftInitialized] = useState(false);
  const showThresholdApplySame = showCheckboxes && isThresholdPane;
  const thresholdApplySameChecked = THRESHOLD_FIELD_KEYS.every(
    (key) => sameForAll[key],
  );

  const visibleFields = useMemo(
    () => getFieldsForPane(activePane),
    [activePane],
  );

  const fieldErrors = useMemo(() => {
    if (!isDraftInitialized) {
      return {};
    }
    const errors: Partial<Record<FieldKey, string>> = {};
    for (const { key, readonly } of visibleFields) {
      if (readonly) continue;
      const rule = VALIDATION_RULES[key];
      if (!rule) continue;
      const checkAll = !showThresholdApplySame || !sameForAll[key];
      const units = checkAll ? orderedPriceUnits : [orderedPriceUnits[0]];
      for (const priceUnit of units) {
        if (!priceUnit) break;
        const fieldValue = draftByPriceUnit[priceUnit.id]?.[key] ?? '';
        if (!rule.isValid(fieldValue)) {
          errors[key] = rule.message;
          break;
        }
      }
    }
    return errors;
  }, [
    draftByPriceUnit,
    isDraftInitialized,
    orderedPriceUnits,
    sameForAll,
    visibleFields,
    showThresholdApplySame,
  ]);

  const hasErrors = Object.keys(fieldErrors).length > 0;

  useEffect(() => {
    const wasOpen = prevIsOpenRef.current;
    prevIsOpenRef.current = isOpen;

    if (!isOpen) {
      setIsDraftInitialized(false);
      return;
    }

    if (wasOpen) return;

    setActivePane('settings');

    const nextDraft: Record<string, PriceSettingsValues> = {};
    for (const [priceUnitId, settings] of pricelistSettings.entries()) {
      nextDraft[priceUnitId] = toDraftValues(settings);
    }

    if (orderedPriceUnits.length >= 2) {
      const firstPriceUnit = orderedPriceUnits[0];
      if (firstPriceUnit) {
        const snapshots: Partial<Record<FieldKey, Record<string, string>>> = {};
        for (const { key } of PRICE_SETTINGS_FIELDS) {
          if (sameForAll[key]) {
            const snapshot: Record<string, string> = {};
            const firstValue = nextDraft[firstPriceUnit.id]?.[key] ?? '0';
            for (const priceUnit of orderedPriceUnits) {
              snapshot[priceUnit.id] = nextDraft[priceUnit.id]?.[key] ?? '0';
              const draft = nextDraft[priceUnit.id];
              if (draft) {
                draft[key] = firstValue;
              }
            }
            snapshots[key] = snapshot;
          }
        }
        preSameForAllSnapshot.current = snapshots;
      }
    } else {
      preSameForAllSnapshot.current = {};
    }

    setDraftByPriceUnit(nextDraft);
    setIsDraftInitialized(true);
  }, [isOpen, orderedPriceUnits, pricelistSettings, sameForAll]);

  const updateValue = (
    priceUnitId: string,
    key: FieldKey,
    rawValue: string,
  ) => {
    let sanitized = filterNumeric(rawValue);

    if (rawValue.trim().startsWith('-') && sanitized) {
      sanitized = `-${sanitized}`;
    }
    setDraftByPriceUnit((prev) => {
      const next = { ...prev };
      if (sameForAll[key] && showCheckboxes) {
        for (const priceUnit of orderedPriceUnits) {
          next[priceUnit.id] = {
            ...(next[priceUnit.id] ??
              toDraftValues(pricelistSettings.get(priceUnit.id))),
            [key]: sanitized,
          };
        }
      } else {
        next[priceUnitId] = {
          ...(next[priceUnitId] ??
            toDraftValues(pricelistSettings.get(priceUnitId))),
          [key]: sanitized,
        };
      }
      return next;
    });
  };

  const handleSameForAllToggle = (key: FieldKey, checked: boolean) => {
    setSameForAll((prev) => ({ ...prev, [key]: checked }));
    if (checked && orderedPriceUnits.length >= 2) {
      const firstPriceUnit = orderedPriceUnits[0];
      if (!firstPriceUnit) return;

      setDraftByPriceUnit((prev) => {
        const snapshot: Record<string, string> = {};
        const firstValue =
          prev[firstPriceUnit.id]?.[key] ??
          formatDecimal(
            pricelistSettings.get(firstPriceUnit.id)?.[key] ?? 0,
            10,
          );
        const next = { ...prev };
        for (const priceUnit of orderedPriceUnits) {
          snapshot[priceUnit.id] =
            prev[priceUnit.id]?.[key] ??
            formatDecimal(pricelistSettings.get(priceUnit.id)?.[key] ?? 0, 10);
          next[priceUnit.id] = {
            ...(next[priceUnit.id] ??
              toDraftValues(pricelistSettings.get(priceUnit.id))),
            [key]: firstValue,
          };
        }
        preSameForAllSnapshot.current[key] = snapshot;
        return next;
      });
    } else if (!checked) {
      const snapshot = preSameForAllSnapshot.current[key];
      setDraftByPriceUnit((prev) => {
        const next = { ...prev };
        for (let i = 1; i < orderedPriceUnits.length; i++) {
          const priceUnit = orderedPriceUnits[i];
          if (!priceUnit) continue;
          next[priceUnit.id] = {
            ...(next[priceUnit.id] ??
              toDraftValues(pricelistSettings.get(priceUnit.id))),
            [key]:
              snapshot?.[priceUnit.id] ??
              formatDecimal(
                pricelistSettings.get(priceUnit.id)?.[key] ?? 0,
                10,
              ),
          };
        }
        return next;
      });
      delete preSameForAllSnapshot.current[key];
    }
  };

  const handleApply = async () => {
    if (hasErrors) return;
    const updatedMap = new Map(pricelistSettings);
    const changedRows: Array<
      { priceUnitId: number } & Partial<Record<FieldKey, number>>
    > = [];

    for (const { id } of orderedPriceUnits) {
      const current = updatedMap.get(id);
      if (!current) continue;

      const draft = draftByPriceUnit[id] ?? toDraftValues(current);
      const parsedFields: Partial<Record<FieldKey, number>> = {};
      let hasChanged = false;

      for (const { key, readonly, isPercentage } of PRICE_SETTINGS_FIELDS) {
        if (readonly) continue;
        const parsedRaw = parseValue(
          draft[key],
          isPercentage ? (current[key] ?? 0) * 100 : current[key],
        );
        const parsed = isPercentage ? parsedRaw / 100 : parsedRaw;
        parsedFields[key] = parsed;
        if (parsed !== current[key]) hasChanged = true;
      }

      if (hasChanged) {
        updatedMap.set(id, { ...current, ...parsedFields });
        changedRows.push({ priceUnitId: Number(id), ...parsedFields });
      }
    }

    if (changedRows.length > 0) {
      await savePricelistSettings({
        priceSettings: changedRows,
      });
    }
    onApply(updatedMap);
  };

  const handleThresholdApplySameToggle = (checked: boolean) => {
    for (const key of THRESHOLD_FIELD_KEYS) {
      if (sameForAll[key] !== checked) {
        handleSameForAllToggle(key, checked);
      }
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={isPending ? () => {} : onClose}
      size={modalSize}
      style={{ maxWidth: estimatedModalWidth }}
      triggerRef={triggerRef}
    >
      <Modal.Title>Price list settings</Modal.Title>
      <Modal.Content>
        <Flex justifyContent="flex-end" marginBottom="24px">
          <ContentSwitcher
            aria-label="Price settings sections"
            variant="emphasis"
            width="fill"
            size="small"
            style={
              {
                width: `${CONTENT_SWITCHER_WIDTH}px`,
                '--ds-color-interactive-neutral-enabled':
                  'rgba(0, 8, 54, 0.08)',
                '--ds-color-interactive-primary-enabled':
                  'rgba(255, 255, 255, 0.85)',
                '--ds-color-content-default': 'rgba(59, 61, 76, 1)',
                '--ds-color-content-on-primary': 'rgba(20, 20, 20, 1)',
              } as CSSProperties
            }
            onSwitch={(value) => setActivePane(value as PriceSettingsPane)}
          >
            <ContentSwitcher.Segment
              selected={activePane === 'settings'}
              aria-controls="price-settings-pane-panel"
              text="Settings"
              value="settings"
            />
            <ContentSwitcher.Segment
              selected={activePane === 'thresholds'}
              aria-controls="price-settings-pane-panel"
              text="Thresholds"
              value="thresholds"
            />
          </ContentSwitcher>
        </Flex>

        <div style={{ paddingBottom: '8px' }}>
          <div
            id="price-settings-pane-panel"
            style={{
              display: 'grid',
              gridTemplateColumns: `${LABEL_COL_WIDTH}px repeat(${pricelistCount}, ${VALUE_COL_WIDTH}px)`,
              columnGap: `${GAP}px`,
              rowGap: `${GAP}px`,
              boxSizing: 'border-box',
            }}
          >
            <div
              style={{
                position: 'sticky',
                left: 0,
                zIndex: 3,
                backgroundColor: 'white',
                minWidth: `${LABEL_COL_WIDTH}px`,
              }}
            />
            {orderedPriceUnits.map((priceUnit) => (
              <div
                key={`header-${priceUnit.id}`}
                style={{
                  height: '40px',
                  color: themeColors.content.default,
                  backgroundColor: 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  fontSize: '14px',
                  fontWeight: 500,
                }}
              >
                {priceUnit.label}
              </div>
            ))}
            {visibleFields.map((field) => {
              const rowBg = field.readonly
                ? 'var(--ds-color-interactive-primary-subtle-selected)'
                : undefined;
              return (
                <Fragment key={field.key}>
                  <Flex
                    position="sticky"
                    left={0}
                    zIndex={3}
                    backgroundColor={rowBg ?? 'transparent'}
                    minWidth={`${LABEL_COL_WIDTH}px`}
                    alignItems="center"
                    justifyContent="flex-end"
                    paddingRight="16px"
                    fontSize="16px"
                    fontWeight={500}
                    color={themeColors.content.default}
                    whiteSpace="nowrap"
                    style={
                      rowBg
                        ? { boxShadow: `0 0 0 ${GAP / 2}px ${rowBg}` }
                        : undefined
                    }
                  >
                    {field.label}
                  </Flex>
                  {orderedPriceUnits.map((priceUnit, idx) => (
                    <div
                      key={`${priceUnit.id}-${field.key}`}
                      style={{
                        width: `${VALUE_COL_WIDTH}px`,
                        backgroundColor: rowBg,
                        ...(rowBg
                          ? { boxShadow: `0 0 0 ${GAP / 2}px ${rowBg}` }
                          : undefined),
                      }}
                    >
                      {field.readonly ? (
                        <Flex
                          height="40px"
                          alignItems="center"
                          justifyContent="flex-end"
                          paddingRight="8px"
                          fontSize="16px"
                          color={themeColors.content.default}
                        >
                          {draftByPriceUnit[priceUnit.id]?.[field.key] ?? ''}
                          {field.isPercentage && ' %'}
                        </Flex>
                      ) : (
                        <div style={{ position: 'relative', width: '100%' }}>
                          <TextField
                            renderEndEnhancer={
                              field.isPercentage
                                ? () => (
                                    <TextField.Enhancer
                                      iconName="percent"
                                      aria-label="percent"
                                    />
                                  )
                                : undefined
                            }
                            value={
                              draftByPriceUnit[priceUnit.id]?.[field.key] ?? ''
                            }
                            onChange={(e) =>
                              updateValue(
                                priceUnit.id,
                                field.key,
                                e.target.value,
                              )
                            }
                            disabled={
                              showThresholdApplySame &&
                              sameForAll[field.key] &&
                              idx > 0
                            }
                            validationState={
                              isDraftInitialized &&
                              VALIDATION_RULES[field.key]?.isValid(
                                draftByPriceUnit[priceUnit.id]?.[field.key] ??
                                  '',
                              ) === false
                                ? 'invalid'
                                : undefined
                            }
                            aria-label={`Price setting ${field.key} for unit ${priceUnit.id}`}
                            height="40px"
                            style={{
                              width: '100%',
                              textAlign: 'right',
                              fontWeight: 500,
                              color: themeColors.content.primary,
                            }}
                            color={themeColors.content.primary}
                          />
                        </div>
                      )}
                    </div>
                  ))}
                  {fieldErrors[field.key] && (
                    <>
                      <div
                        style={{
                          position: 'sticky',
                          left: 0,
                          zIndex: 3,
                          backgroundColor: 'white',
                          minWidth: `${LABEL_COL_WIDTH}px`,
                        }}
                      />
                      <div
                        style={{
                          gridColumn: '2 / -1',
                          fontSize: '11px',
                          color: themeColors.support.negative.default,
                        }}
                      >
                        {fieldErrors[field.key]}
                      </div>
                    </>
                  )}
                </Fragment>
              );
            })}
            {showThresholdApplySame && (
              <>
                <div
                  style={{
                    position: 'sticky',
                    left: 0,
                    backgroundColor: 'white',
                    zIndex: 3,
                    minWidth: `${LABEL_COL_WIDTH}px`,
                  }}
                />
                <Checkbox
                  label="Apply same values"
                  aria-label="Apply same values"
                  checked={thresholdApplySameChecked}
                  style={{
                    padding: '4px 0 0 0',
                    gridColumn: '2 / 3',
                    zIndex: 0,
                  }}
                  onChange={(e) =>
                    handleThresholdApplySameToggle(e.target.checked)
                  }
                />
              </>
            )}
          </div>
        </div>
      </Modal.Content>

      <Modal.Footer>
        <Button
          aria-label="Cancel"
          appearance="ghost"
          variant="neutral"
          onClick={onClose}
          disabled={isPending}
        >
          Cancel
        </Button>
        <Button
          aria-label="Apply"
          appearance="solid"
          onClick={handleApply}
          loading={isPending}
          disabled={hasErrors}
        >
          Apply
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PriceSettingsModal;
